import enMessages from '@/plugins/i18n/locales/en.json'

// Top-level i18n namespaces excluded from seeding.
// $vuetify holds Vuetify-internal labels, not user-facing content.
const EXCLUDED_TOP_LEVEL_KEYS = new Set(['$vuetify'])

const HTML_TAG_RE = /<\/?[a-z][\s\S]*?>/i

// Curated seed items — hand-written displayName/description/category.
// These take precedence over auto-derived entries for the same key.
const curatedSeedItems = [
  {
    key: 'home.header.headline',
    value: 'Preferred Reservations',
    contentType: 'text',
    displayName: 'Home Header Headline',
    description: 'Main headline in the home header bar',
    category: 'marketing',
  },
  {
    key: 'home.header.subtitle',
    value: 'trusted by Artists, Athletes, CEOs & Restaurants',
    contentType: 'text',
    displayName: 'Home Header Subtitle',
    description: 'Subtitle text in the home header bar',
    category: 'marketing',
  },
  {
    key: 'home.header.discoverLink',
    value: '1,000+ favorites globally, 100+ cities',
    contentType: 'text',
    displayName: 'Home Header Discover Link',
    description: 'Link text below the home header subtitle',
    category: 'marketing',
  },
  {
    key: 'home.masthead.headline',
    // The hero sentence (home + all activation pages). The inline spans are
    // styled by HomeMasthead: .amp quiets each "&", .nb keeps "& <word>"
    // together so an "&" never ends a line. Deliberately contentType 'text'
    // (not richtext) so admins edit the markup verbatim in a plain field —
    // the Tiptap richtext editor would strip the span classes. CmsText still
    // renders it as HTML because isRichText() detects tags in the value.
    value: 'Artists, Athletes, CEOs, Investors <span class="nb"><span class="amp">&</span> Restaurateurs</span> who love dining at their favorites <span class="nb"><span class="amp">&</span> who</span> restaurants love hosting',
    contentType: 'text',
    displayName: 'Masthead Headline',
    description: 'The hero sentence on the home page and activation pages',
    category: 'marketing',
  },
  {
    key: 'home.masthead.subtitle',
    value: 'RM is a network of Artists, Athletes, CEOs, Investors & Restaurateurs who love dining out',
    contentType: 'text',
    displayName: 'Masthead Subtitle',
    description: 'Subtitle under the masthead headline (hero sub-line 1)',
    category: 'marketing',
  },
  {
    key: 'home.masthead.subtitle2',
    value: "Restaurants love hosting RM's high-profile/high-spending members",
    contentType: 'text',
    displayName: 'Masthead Subtitle 2',
    description: 'Second hero sub-line under the masthead headline (home page only)',
    category: 'marketing',
  },
  {
    key: 'home.masthead.aiLine',
    value: "Members use RM's AI-powered app for hard-to-get reservations & discovery at their favorite/top restaurants globally",
    contentType: 'text',
    displayName: 'Masthead AI Line',
    description: 'AI app line shown on the personal referral (friends) page only',
    category: 'marketing',
  },
  {
    key: 'home.masthead.membersLink',
    value: 'For Members',
    contentType: 'text',
    displayName: 'Masthead Members Link',
    description: 'Members link text in masthead',
    category: 'navigation',
  },
  {
    key: 'home.masthead.restaurantsLink',
    value: 'For Restaurants',
    contentType: 'text',
    displayName: 'Masthead Restaurants Link',
    description: 'Restaurants link text in masthead',
    category: 'navigation',
  },
  {
    key: 'home.masthead.iosNote',
    value: 'iOS app available post activation',
    contentType: 'text',
    displayName: 'Masthead iOS Note',
    description: 'iOS availability note in masthead',
    category: 'marketing',
  },
  {
    key: 'home.masthead.platinumPrefix',
    value: 'free of Platinum',
    contentType: 'text',
    displayName: 'Masthead Platinum Prefix',
    description: 'Platinum prefix text in masthead',
    category: 'marketing',
  },
  {
    key: 'home.overview.membersTitle',
    value: 'members',
    contentType: 'text',
    displayName: 'Overview Members Title',
    description: 'Members section title on home page',
    category: 'marketing',
  },
  {
    key: 'home.overview.sportsEntText',
    value: '<b>Sports & Entertainment:</b> Artists, Athletes, & Team Execs/Owners',
    contentType: 'richtext',
    displayName: 'Overview Sports & Ent Text',
    description: 'Sports & entertainment description in overview',
    category: 'marketing',
  },
  {
    key: 'home.overview.luxuryDinersText',
    value: '<b>Luxury Diners:</b> Executives, Investors & Dining Enthusiasts',
    contentType: 'richtext',
    displayName: 'Overview Luxury Diners Text',
    description: 'Luxury diners description in overview',
    category: 'marketing',
  },
  {
    key: 'home.overview.trustTitle',
    value: 'trust RM for',
    contentType: 'text',
    displayName: 'Overview Trust Title',
    description: 'Trust section title in overview',
    category: 'marketing',
  },
  {
    key: 'home.overview.preferredText',
    value: '<b>Preferred reservations:</b> accessing peak, last minute times at favorites N/A on OpenTable, Resy, etc. (i.e. 7pm on Fridays) without relying on favors & wait-lists',
    contentType: 'richtext',
    displayName: 'Overview Preferred Text',
    description: 'Preferred reservations description',
    category: 'marketing',
  },
  {
    key: 'home.overview.eventsText',
    value: '<b>Simplified event bookings:</b> 1 inquiry to 10 places to get best fit vs contacting each individually',
    contentType: 'richtext',
    displayName: 'Overview Events Text',
    description: 'Events booking description',
    category: 'marketing',
  },
  {
    key: 'home.overview.insightsText',
    value: '<b>Global dining insights:</b> tracking members trending & favorites',
    contentType: 'richtext',
    displayName: 'Overview Insights Text',
    description: 'Dining insights description',
    category: 'marketing',
  },
  {
    key: 'home.overview.viewMoreLink',
    value: 'view more here',
    contentType: 'text',
    displayName: 'Overview View More Link',
    description: 'View more link text in overview',
    category: 'navigation',
  },
  {
    key: 'home.testimonials.title',
    value: 'testimonials',
    contentType: 'text',
    displayName: 'Testimonials Section Title',
    description: 'Title for testimonials section',
    category: 'testimonial',
  },
  {
    key: 'toast.joinTitle',
    value: 'join RM',
    contentType: 'text',
    displayName: 'Toast Join Title',
    description: 'Title for the join RM toast popup',
    category: 'marketing',
  },
  {
    key: 'toast.joinBody',
    value: 'join RM for preferred reservations & simplified event bookings at your favorite restaurants globally - 1,000+, 100+ cities',
    contentType: 'text',
    displayName: 'Toast Join Body',
    description: 'Body text for the join RM toast popup',
    category: 'marketing',
  },
  {
    key: 'toast.joinBtn',
    value: 'join now',
    contentType: 'text',
    displayName: 'Toast Join Button',
    description: 'Join button text in toast popup',
    category: 'action',
  },
  {
    key: 'toast.loginBtn',
    value: 'log in',
    contentType: 'text',
    displayName: 'Toast Login Button',
    description: 'Login button text in toast popup',
    category: 'action',
  },

  // Navigation labels (Footer + Desktop nav)
  {
    key: 'nav.discover',
    value: 'Discover',
    contentType: 'text',
    displayName: 'Nav: Discover',
    description: 'Bottom/desktop nav tab label for Discover',
    category: 'navigation',
  },
  {
    key: 'nav.bookings',
    value: 'Bookings',
    contentType: 'text',
    displayName: 'Nav: Bookings',
    description: 'Bottom/desktop nav tab label for Bookings',
    category: 'navigation',
  },
  {
    key: 'nav.favorites',
    value: 'Favorites',
    contentType: 'text',
    displayName: 'Nav: Favorites',
    description: 'Bottom/desktop nav tab label for Favorites',
    category: 'navigation',
  },
  {
    key: 'nav.ra',
    value: 'RA',
    contentType: 'text',
    displayName: 'Nav: RA',
    description: 'Bottom/desktop nav tab label for Restaurant Admin',
    category: 'navigation',
  },
  {
    key: 'nav.login',
    value: 'Login',
    contentType: 'text',
    displayName: 'Nav: Login',
    description: 'Bottom/desktop nav label for Login',
    category: 'navigation',
  },
  {
    key: 'nav.profile',
    value: 'Profile',
    contentType: 'text',
    displayName: 'Nav: Profile',
    description: 'Bottom/desktop nav label for Profile',
    category: 'navigation',
  },
  {
    key: 'nav.terms',
    value: 'Terms',
    contentType: 'text',
    displayName: 'Nav: Terms',
    description: 'Footer link for Terms page',
    category: 'navigation',
  },
  {
    key: 'nav.privacy',
    value: 'Privacy',
    contentType: 'text',
    displayName: 'Nav: Privacy',
    description: 'Footer link for Privacy page',
    category: 'navigation',
  },

  // Menu items (ProfileMenu + UserMenu)
  {
    key: 'menu.discoverFriendsFavorites',
    value: "Discover Friends' Favorites",
    contentType: 'text',
    displayName: 'Menu: Discover Friends Favs',
    description: 'Profile menu link to network/friends page',
    category: 'navigation',
  },
  {
    key: 'menu.giveFriendsFree',
    value: 'Give Friends 3 Months Free',
    contentType: 'text',
    displayName: 'Menu: Give Friends Free',
    description: 'Profile menu referral link text',
    category: 'navigation',
  },
  {
    key: 'menu.membershipDetails',
    value: 'Membership details',
    contentType: 'text',
    displayName: 'Menu: Membership Details',
    description: 'Profile/user menu link to membership page',
    category: 'navigation',
  },
  {
    key: 'menu.activityBookings',
    value: 'Activity/Bookings (Beta)',
    contentType: 'text',
    displayName: 'Menu: Activity/Bookings',
    description: 'Menu link to reservations review page',
    category: 'navigation',
  },
  {
    key: 'menu.restaurantPages',
    value: 'Restaurant Pages (Beta)',
    contentType: 'text',
    displayName: 'Menu: Restaurant Pages',
    description: 'Section header for restaurant pages in menu',
    category: 'navigation',
  },
  {
    key: 'menu.logout',
    value: 'Logout',
    contentType: 'text',
    displayName: 'Menu: Logout',
    description: 'Logout button text in menus',
    category: 'action',
  },
  {
    key: 'menu.exitSimulation',
    value: 'Return to Admin Account',
    contentType: 'text',
    displayName: 'Menu: Return to Admin Account',
    description: 'Label shown to admins to leave a member simulation and return to their admin account',
    category: 'action',
  },
  {
    key: 'menu.faqs',
    value: 'FAQs',
    contentType: 'text',
    displayName: 'Menu: FAQs',
    description: 'FAQs footer link in profile menu',
    category: 'navigation',
  },
  {
    key: 'menu.partners',
    value: 'Partners',
    contentType: 'text',
    displayName: 'Menu: Partners',
    description: 'Partners footer link in profile menu',
    category: 'navigation',
  },
  {
    key: 'menu.restaurants',
    value: 'Restaurants',
    contentType: 'text',
    displayName: 'Menu: Restaurants',
    description: 'Restaurants footer link in profile menu',
    category: 'navigation',
  },
  {
    key: 'menu.home',
    value: 'Home',
    contentType: 'text',
    displayName: 'Menu: Home',
    description: 'Home link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.howRmWorks',
    value: 'How RM works & FAQs',
    contentType: 'text',
    displayName: 'Menu: How RM Works',
    description: 'How RM works link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.luxuryPartners',
    value: 'Luxury Partners',
    contentType: 'text',
    displayName: 'Menu: Luxury Partners',
    description: 'Luxury Partners link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.preferredRestaurants',
    value: 'Preferred Restaurants',
    contentType: 'text',
    displayName: 'Menu: Preferred Restaurants',
    description: 'Preferred Restaurants link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.secondaryAccount',
    value: ' Spouse/Partner Account',
    contentType: 'text',
    displayName: 'Menu: Secondary Account',
    description: 'Secondary Account link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.profileAndSettings',
    value: 'Profile & Settings',
    contentType: 'text',
    displayName: 'Menu: Profile & Settings',
    description: 'Profile & Settings link in user menu',
    category: 'navigation',
  },
  {
    key: 'menu.members',
    value: 'Manage Group Members',
    contentType: 'text',
    displayName: 'Menu: Members',
    description: 'Members link in user menu group section',
    category: 'navigation',
  },
  {
    key: 'menu.support',
    value: 'Support',
    contentType: 'text',
    displayName: 'Menu: Support',
    description: 'Support link in user menu group section',
    category: 'navigation',
  },
  {
    key: 'menu.adminSettings',
    value: 'Admin Settings',
    contentType: 'text',
    displayName: 'Menu: Admin Settings Header',
    description: 'Admin Settings section header in menus',
    category: 'navigation',
  },
  {
    key: 'menu.emailTemplates',
    value: 'Email Templates',
    contentType: 'text',
    displayName: 'Menu: Email Templates',
    description: 'Email Templates admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.groupAccounts',
    value: 'Group accounts',
    contentType: 'text',
    displayName: 'Menu: Group Accounts',
    description: 'Group accounts admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.instantBookings',
    value: 'Instant Bookings',
    contentType: 'text',
    displayName: 'Menu: Instant Bookings',
    description: 'Instant Bookings admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.memberAccounts',
    value: 'Member accounts',
    contentType: 'text',
    displayName: 'Menu: Member Accounts',
    description: 'Member accounts admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.restaurantDatabase',
    value: 'Restaurant database',
    contentType: 'text',
    displayName: 'Menu: Restaurant Database',
    description: 'Restaurant database admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.settings',
    value: 'Settings',
    contentType: 'text',
    displayName: 'Menu: Settings',
    description: 'Settings admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.tags',
    value: 'Tags',
    contentType: 'text',
    displayName: 'Menu: Tags',
    description: 'Tags admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.cmsContent',
    value: 'CMS Content',
    contentType: 'text',
    displayName: 'Menu: CMS Content',
    description: 'CMS Content admin menu link',
    category: 'navigation',
  },
  {
    key: 'menu.requestToJoin',
    value: 'Request to Join',
    contentType: 'text',
    displayName: 'Menu: Request to Join',
    description: 'Request to Join button text',
    category: 'action',
  },
  {
    key: 'menu.logIn',
    value: 'Log in',
    contentType: 'text',
    displayName: 'Menu: Log In',
    description: 'Log in button text in menus',
    category: 'action',
  },
  {
    key: 'menu.referral.nominateInfo',
    value: "Have friends restaurants love hosting too? They can sign up for 3 months free of Platinum. The more great members, the better for all.",
    contentType: 'text',
    displayName: 'Menu: Nominate Friends Info',
    description: 'Info text in the nominate friends section',
    category: 'marketing',
  },
  {
    key: 'menu.referral.referralMessage',
    value: "Friends - I'm a member of RM, a network of Artists, Athletes, CEOs, Investors & Restaurateurs who highly value where they dine. I use RM's app for hard-to-get reservations & elevated experiences at favorite/top places globally. Join here for 3 months free of Platinum:",
    contentType: 'text',
    displayName: 'Menu: Referral Message',
    description: 'Referral message text for sharing with friends',
    category: 'marketing',
  },

  // Discover page content
  {
    key: 'discover.tabs.membersFavorites',
    value: "Members' Favorites",
    contentType: 'text',
    displayName: 'Discover Tab: Members Favs',
    description: 'Tab label for Members Favorites on discover page',
    category: 'navigation',
  },
  {
    key: 'discover.tabs.instantBookings',
    value: 'Instant Bookings',
    contentType: 'text',
    displayName: 'Discover Tab: Instant Bookings',
    description: 'Tab label for Instant Bookings on discover page',
    category: 'navigation',
  },
  {
    key: 'discover.filters.az',
    value: 'A-Z',
    contentType: 'text',
    displayName: 'Discover Filter: A-Z',
    description: 'A-Z filter option on discover page',
    category: 'filter',
  },
  {
    key: 'discover.filters.justBooked',
    value: 'Booked',
    contentType: 'text',
    displayName: 'Discover Filter: Booked',
    description: 'Booked filter option on discover page ("Just Booked" until RM-399, 10/12)',
    category: 'filter',
  },
  {
    key: 'discover.filters.preferred',
    value: 'Preferred',
    contentType: 'text',
    displayName: 'Discover Filter: Preferred',
    description: 'Preferred filter option on discover page',
    category: 'filter',
  },
  {
    key: 'discover.filters.groupPrivate',
    value: 'Group/Private',
    contentType: 'text',
    displayName: 'Discover Filter: Group/Private',
    description: 'Group/Private filter option on discover page',
    category: 'filter',
  },
  {
    key: 'discover.filters.trending',
    value: 'Trending',
    contentType: 'text',
    displayName: 'Discover Filter: Trending',
    description: 'Trending filter option on discover page',
    category: 'filter',
  },
  {
    key: 'discover.filters.network',
    value: 'Network',
    contentType: 'text',
    displayName: 'Discover Filter: Network',
    description: 'Network filter tab on discover page (your connections\' favorites)',
    category: 'filter',
  },
  {
    key: 'discover.network.contextHeading',
    value: "Your network's favorites",
    contentType: 'text',
    displayName: 'Discover: Network Context Heading',
    description: 'Heading shown above results when the Network tab is selected (logged-in only)',
    category: 'marketing',
  },
  {
    key: 'discover.gated.justBookedLine',
    value: 'Viewable by members only',
    contentType: 'text',
    displayName: 'Discover: Booked Gate Line',
    description: 'Serif gate line on the Booked tab for logged-out visitors (never say "Platinum" to prospects)',
    category: 'marketing',
  },
  {
    key: 'discover.gated.justBookedStatRestaurants',
    value: '300+ restaurants',
    contentType: 'text',
    displayName: 'Discover: Booked Gate Stat (Restaurants)',
    description: 'First half of the quiet stat under the Booked gate line (restaurants first)',
    category: 'marketing',
  },
  {
    key: 'discover.gated.justBookedStatCities',
    value: '50+ cities',
    contentType: 'text',
    displayName: 'Discover: Booked Gate Stat (Cities)',
    description: 'Second half of the quiet stat under the Booked gate line',
    category: 'marketing',
  },
  {
    key: 'discover.gated.networkLine',
    value: "Members can view their network's favorite places",
    contentType: 'text',
    displayName: 'Discover: Network Gate Line',
    description: 'Serif gate line on the Network tab for logged-out visitors (no stat — Network is personal)',
    category: 'marketing',
  },
  {
    key: 'discover.preferred.descriptor',
    value: 'Generally respond to requests within 24 hours',
    contentType: 'text',
    displayName: 'Discover: Preferred Tab Descriptor',
    description: 'One-line descriptor under the tab row on the Preferred tab (tappable — opens the For Restaurants modal)',
    category: 'marketing',
  },
  {
    key: 'restaurant.fans.gatedLine',
    value: 'Viewable by members only',
    contentType: 'text',
    displayName: 'Restaurant: Fans Sheet Gate Line',
    description: 'Serif gate line in the fans (heart-count) bottom sheet for logged-out visitors',
    category: 'marketing',
  },
  {
    key: 'footer.memberLine',
    value: 'RM is a network of Artists, Athletes, CEOs, Investors & Restaurateurs who love dining out',
    contentType: 'text',
    displayName: 'Footer Member Line',
    description: 'Canonical member line shown in the desktop footer (no period)',
    category: 'marketing',
  },
  {
    key: 'discover.labels.preferredRestaurants',
    value: 'Preferred Restaurants',
    contentType: 'text',
    displayName: 'Discover: Preferred Label',
    description: 'Preferred Restaurants label on discover page',
    category: 'marketing',
  },
  {
    key: 'discover.labels.groupPrivateSpaces',
    value: 'Group/Private Spaces',
    contentType: 'text',
    displayName: 'Discover: Group/Private Label',
    description: 'Group/Private Spaces label on discover page',
    category: 'marketing',
  },
  {
    key: 'discover.labels.cantFindFavorite',
    value: "Can't find your favorite?",
    contentType: 'text',
    displayName: 'Discover: Add Favorite CTA',
    description: 'CTA text for adding a restaurant on discover page',
    category: 'marketing',
  },
  {
    key: 'discover.labels.addRestaurant',
    value: 'Add Restaurant',
    contentType: 'text',
    displayName: 'Discover: Add Restaurant Btn',
    description: 'Add Restaurant button text on discover page',
    category: 'action',
  },
  {
    key: 'discover.labels.instantBookingsElevated',
    value: 'Instant Bookings for Elevated Experiences',
    contentType: 'text',
    displayName: 'Discover: IB Tagline',
    description: 'Instant Bookings tagline on discover page',
    category: 'marketing',
  },
  {
    key: 'discover.labels.submitRequestOther',
    value: 'Submit request for other dates/restaurants',
    contentType: 'text',
    displayName: 'Discover: Submit Request CTA',
    description: 'Submit request CTA on instant bookings tab',
    category: 'action',
  },
  {
    key: 'discover.labels.moreTablesComing',
    value: 'More tables coming soon',
    contentType: 'text',
    displayName: 'Discover: More Tables Coming',
    description: 'More tables coming text on instant bookings tab',
    category: 'marketing',
  },
  {
    key: 'discover.labels.loadingMore',
    value: 'Loading more...',
    contentType: 'text',
    displayName: 'Discover: Loading More',
    description: 'Loading more text while prefetching',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.noBookingsCity',
    value: 'No bookings yet in this city',
    contentType: 'text',
    displayName: 'Discover: No Bookings City',
    description: 'Empty state when no bookings for selected city',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.filterByAZ',
    value: "Filter by A-Z to view Members' Favorites",
    contentType: 'text',
    displayName: 'Discover: Filter by A-Z hint',
    description: 'Hint text to use A-Z filter in empty states',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.noGroupSpaces',
    value: 'No group/private dining spaces featured yet for this city',
    contentType: 'text',
    displayName: 'Discover: No Group Spaces',
    description: 'Empty state for Group/Private filter with no results',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.noPreferredCity',
    value: 'No Preferred Restaurants yet for this city',
    contentType: 'text',
    displayName: 'Discover: No Preferred City',
    description: 'Empty state for Preferred filter with no results',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.noRestaurantsFound',
    value: 'No restaurants found',
    contentType: 'text',
    displayName: 'Discover: No Restaurants',
    description: 'Generic empty state for no restaurants found',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.allTablesBooked',
    value: 'All Tables Booked',
    contentType: 'text',
    displayName: 'Discover: All Tables Booked',
    description: 'Empty state heading for instant bookings',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.premiumTablesSecured',
    value: 'Premium tables have been secured by members',
    contentType: 'text',
    displayName: 'Discover: Tables Secured',
    description: 'Empty state description for instant bookings',
    category: 'empty-state',
  },
  {
    key: 'discover.emptyState.loadingSlots',
    value: 'Loading available slots...',
    contentType: 'text',
    displayName: 'Discover: Loading Slots',
    description: 'Loading text for instant booking slots',
    category: 'empty-state',
  },

  // Favorites page
  {
    key: 'favorites.emptyState',
    value: 'Restaurants you favorite will appear here',
    contentType: 'text',
    displayName: 'Favorites: Empty State',
    description: 'Empty state message on favorites page',
    category: 'empty-state',
  },
  {
    key: 'favorites.preferred.help',
    value: 'Restaurants can designate members as Preferred - enabling reservation requests without a guaranteed spend.<br/>Add your favorite restaurants & complete your profile so restaurants can view your profile.',
    contentType: 'text',
    displayName: 'Preferred Status on Favorites',
    description: 'Help text for "Preferred Status" filter',
    category: 'empty-state',
  },
  {
    key: 'favorites.noFilterResults',
    value: 'No Favorites for selected filter',
    contentType: 'text',
    displayName: 'Favorites: No Filter Results',
    description: 'Empty state when no favorites match the selected filter',
    category: 'empty-state',
  },

  // Bookings page
  {
    key: 'bookings.tab',
    value: 'Bookings',
    contentType: 'text',
    displayName: 'Bookings: Tab Label',
    description: 'Tab label on bookings page',
    category: 'navigation',
  },
  {
    key: 'bookings.newReservationCta',
    value: 'Reservation or Group/Private Dining',
    contentType: 'text',
    displayName: 'Bookings: New Reservation CTA',
    description: 'CTA text for creating a new reservation',
    category: 'action',
  },
  {
    key: 'bookings.emptyState',
    value: 'Reservations & Events via RM will show here',
    contentType: 'text',
    displayName: 'Bookings: Empty State',
    description: 'Main empty state message on bookings page',
    category: 'empty-state',
  },
  {
    key: 'bookings.emptyStateInner',
    value: 'Bookings you make will appear here',
    contentType: 'text',
    displayName: 'Bookings: Inner Empty State',
    description: 'Empty state message within Active group',
    category: 'empty-state',
  },
  {
    key: 'bookings.groups.active',
    value: 'Active',
    contentType: 'text',
    displayName: 'Bookings: Active Group',
    description: 'Active group heading on bookings page',
    category: 'navigation',
  },
  {
    key: 'bookings.groups.experienced',
    value: 'Experienced',
    contentType: 'text',
    displayName: 'Bookings: Experienced Group',
    description: 'Experienced group heading on bookings page',
    category: 'navigation',
  },
  {
    key: 'bookings.groups.canceledOrNA',
    value: 'Submitted or Not Available',
    contentType: 'text',
    displayName: 'Bookings: Submitted/Not Available Group',
    description: 'Submitted or Not Available group heading on bookings page (past submitted group/private inquiries, Not Available, Rescinded and Canceled requests)',
    category: 'navigation',
  },

  // Reservation dialog labels
  {
    key: 'reservation.labels.date',
    value: 'Date',
    contentType: 'text',
    displayName: 'Reservation: Date Label',
    description: 'Date field label in reservation dialog',
    category: 'form',
  },
  {
    key: 'reservation.labels.partySize',
    value: 'Party Size',
    contentType: 'text',
    displayName: 'Reservation: Party Size Label',
    description: 'Party Size field label in reservation dialog',
    category: 'form',
  },
  {
    key: 'reservation.labels.time',
    value: 'Time',
    contentType: 'text',
    displayName: 'Reservation: Time Label',
    description: 'Time field label in reservation dialog',
    category: 'form',
  },
  {
    key: 'reservation.labels.earliestStartTime',
    value: 'Earliest Start Time',
    contentType: 'text',
    displayName: 'Reservation: Earliest Time',
    description: 'Earliest Start Time field label',
    category: 'form',
  },
  {
    key: 'reservation.labels.latestStartTime',
    value: 'Latest Start Time',
    contentType: 'text',
    displayName: 'Reservation: Latest Time',
    description: 'Latest Start Time field label',
    category: 'form',
  },
  {
    key: 'reservation.labels.occasion',
    value: 'Occasion',
    contentType: 'text',
    displayName: 'Reservation: Occasion Label',
    description: 'Occasion field label in reservation dialog',
    category: 'form',
  },
  {
    key: 'reservation.labels.helpfulForRestaurant',
    value: 'Helpful for the restaurant to know',
    contentType: 'text',
    displayName: 'Reservation: Helpful Info',
    description: 'Help text above special requests section',
    category: 'help',
  },
  {
    key: 'reservation.labels.preferredRestaurant',
    value: 'Preferred Restaurant',
    contentType: 'text',
    displayName: 'Reservation: Preferred Label',
    description: 'Preferred Restaurant label in reservation',
    category: 'form',
  },
  {
    key: 'reservation.labels.reviewAndSubmit',
    value: 'Review & Submit',
    contentType: 'text',
    displayName: 'Reservation: Review Submit',
    description: 'Review & Submit heading in reservation dialog',
    category: 'action',
  },
  {
    key: 'reservation.instantBookings.subtitle',
    value: 'Instant Bookings for Elevated Experiences',
    contentType: 'text',
    displayName: 'Reservation: IB Subtitle',
    description: 'Subtitle for instant bookings in reservation flow',
    category: 'marketing',
  },
  {
    key: 'reservation.instantBookings.checkingAvailability',
    value: 'Checking availability...',
    contentType: 'text',
    displayName: 'Reservation: Checking Avail',
    description: 'Loading text while checking instant booking slots',
    category: 'empty-state',
  },
  {
    key: 'reservation.payment.howPaymentWorks',
    value: 'How payment works',
    contentType: 'text',
    displayName: 'Reservation: How Payment Works',
    description: 'Section heading for payment info',
    category: 'help',
  },
  {
    key: 'reservation.payment.cancellationPolicy',
    value: 'Cancellation policy',
    contentType: 'text',
    displayName: 'Reservation: Cancel Policy',
    description: 'Cancellation policy section heading',
    category: 'help',
  },
  {
    key: 'reservation.payment.fullRefund',
    value: 'Full refund only if canceled 24 hours prior to reservation time',
    contentType: 'text',
    displayName: 'Reservation: Full Refund',
    description: 'Full refund policy text',
    category: 'help',
  },
  {
    key: 'reservation.payment.cancellationPenalty',
    value: "Cancellations after this time or no shows can result in charge of full hold - solely at restaurant's discretion",
    contentType: 'text',
    displayName: 'Reservation: Cancel Penalty',
    description: 'Cancellation penalty warning text',
    category: 'help',
  },
  {
    key: 'reservation.payment.creditCardForHold',
    value: 'CC hold for guaranteed F&B spend',
    contentType: 'text',
    displayName: 'Reservation: CC for Hold',
    description: 'Credit card for hold section heading',
    category: 'form',
  },
  {
    key: 'reservation.payment.addNewCard',
    value: 'Add New Card',
    contentType: 'text',
    displayName: 'Reservation: Add New Card',
    description: 'Add New Card option in payment selector',
    category: 'action',
  },
  {
    key: 'reservation.payment.saveCardFuture',
    value: 'Save this card for future use',
    contentType: 'text',
    displayName: 'Reservation: Save Card',
    description: 'Checkbox label for saving card for future use',
    category: 'form',
  },
  {
    key: 'reservation.validation.membershipPending',
    value: 'Your membership is pending. Once approved you can submit requests.',
    contentType: 'text',
    displayName: 'Reservation: Membership Pending',
    description: 'Warning shown when user membership is pending',
    category: 'validation',
  },
  {
    key: 'reservation.validation.platinumRequired',
    value: 'Platinum membership required for requests to restaurants that are not Preferred',
    contentType: 'text',
    displayName: 'Reservation: Platinum Required',
    description: 'Warning shown when platinum is needed for non-preferred',
    category: 'validation',
  },
  {
    key: 'reservation.validation.platinumRequiredPreferred',
    value: 'Platinum membership required to submit Preferred Requests',
    contentType: 'text',
    displayName: 'Reservation: Platinum for Preferred',
    description: 'Warning for platinum requirement on preferred requests',
    category: 'validation',
  },
  {
    key: 'reservation.validation.preferredInfo',
    value: "Members' favorites that respond to reservation & private dining requests within 24 hours",
    contentType: 'text',
    displayName: 'Reservation: Preferred Info',
    description: 'Info text about preferred restaurant response times',
    category: 'help',
  },

  // Reservation labels (additional)
  {
    key: 'reservation.labels.preferredTime',
    value: 'Preferred Time',
    contentType: 'text',
    displayName: 'Reservation: Preferred Time',
    description: 'Label for preferred time in reservation review',
    category: 'form',
  },
  {
    key: 'reservation.labels.memberProfile',
    value: 'Member Profile',
    contentType: 'text',
    displayName: 'Reservation: Member Profile',
    description: 'Member profile section heading in reservation review',
    category: 'form',
  },
  {
    key: 'reservation.labels.sampleMemberProfile',
    value: 'Sample Member Profile',
    contentType: 'text',
    displayName: 'Reservation: Sample Profile',
    description: 'Sample member profile heading for dummy users',
    category: 'form',
  },
  {
    key: 'reservation.labels.restaurantsReviewProfile',
    value: '(restaurants review when evaluating requests)',
    contentType: 'text',
    displayName: 'Reservation: Profile Review Note',
    description: 'Note that restaurants review profiles when evaluating',
    category: 'help',
  },
  {
    key: 'reservation.labels.confirmedRestaurant',
    value: 'Confirmed Restaurant',
    contentType: 'text',
    displayName: 'Reservation: Confirmed Restaurant',
    description: 'Admin field label for confirmed restaurant',
    category: 'form',
  },
  {
    key: 'reservation.labels.confirmedTime',
    value: 'Confirmed Time',
    contentType: 'text',
    displayName: 'Reservation: Confirmed Time',
    description: 'Admin field label for confirmed time',
    category: 'form',
  },
  {
    key: 'reservation.labels.fbBudget',
    value: 'F&B Budget',
    contentType: 'text',
    displayName: 'Reservation: F&B Budget',
    description: 'F&B Budget label in private dining review',
    category: 'form',
  },
  {
    key: 'reservation.labels.budgetFlexibilityLabel',
    value: 'Is this your max budget?',
    contentType: 'text',
    displayName: 'Reservation: Budget Flexibility',
    description: 'Budget flexibility question label',
    category: 'form',
  },
  {
    key: 'reservation.labels.noDateSelected',
    value: 'No date selected',
    contentType: 'text',
    displayName: 'Reservation: No Date Selected',
    description: 'Fallback text when no date is selected',
    category: 'form',
  },
  {
    key: 'reservation.labels.specialRequestsOptional',
    value: 'Special Requests (optional)',
    contentType: 'text',
    displayName: 'Reservation: Special Requests Label',
    description: 'Label for special requests textarea',
    category: 'form',
  },
  {
    key: 'reservation.labels.specialRequestWarning',
    value: 'Important - Please Note & Review, Special Requests may reduce the likelihood of restaurant being able to accommodate',
    contentType: 'text',
    displayName: 'Reservation: Special Request Warning',
    description: 'Warning about special requests reducing likelihood',
    category: 'help',
  },

  // Reservation payment (additional)
  {
    key: 'reservation.payment.fbMinimum',
    value: 'F&B minimum',
    contentType: 'text',
    displayName: 'Payment: F&B Minimum',
    description: 'F&B minimum label in spend breakdown',
    category: 'form',
  },
  {
    key: 'reservation.payment.totalHold',
    value: 'Total Hold',
    contentType: 'text',
    displayName: 'Payment: Total Hold',
    description: 'Total Hold label in spend breakdown',
    category: 'form',
  },
  {
    key: 'reservation.payment.holdDescription',
    value: 'temporary credit card hold with RM (F&B minimum + tax & tip). If request not accepted, credit card hold released instantly.',
    contentType: 'text',
    displayName: 'Payment: Hold Description',
    description: 'Description of how the credit card hold works',
    category: 'help',
  },
  {
    key: 'reservation.payment.noCommissions',
    value: 'RM takes no commissions/fees',
    contentType: 'text',
    displayName: 'Payment: No Commissions',
    description: 'Text stating RM takes no commissions',
    category: 'marketing',
  },
  {
    key: 'reservation.payment.preTaxTip',
    value: 'pre-tax & tip',
    contentType: 'text',
    displayName: 'Payment: Pre-Tax & Tip',
    description: 'Pre-tax & tip label',
    category: 'form',
  },
  {
    key: 'reservation.payment.tax',
    value: 'Tax',
    contentType: 'text',
    displayName: 'Payment: Tax Label',
    description: 'Tax label in spend breakdown',
    category: 'form',
  },
  {
    key: 'reservation.payment.gratuity',
    value: 'Gratuity',
    contentType: 'text',
    displayName: 'Payment: Gratuity Label',
    description: 'Gratuity label in spend breakdown',
    category: 'form',
  },
  {
    key: 'reservation.payment.minBudgetRequired',
    value: '$1,000 F&B budget needed to submit via RM',
    contentType: 'text',
    displayName: 'Payment: Min Budget Required',
    description: 'Minimum budget warning for private dining',
    category: 'validation',
  },
  {
    key: 'reservation.payment.budgetCanGoHigher',
    value: 'No, can go higher if needed',
    contentType: 'text',
    displayName: 'Payment: Budget Can Go Higher',
    description: 'Budget flexibility option - can go higher',
    category: 'form',
  },
  {
    key: 'reservation.payment.budgetIsMax',
    value: 'Yes, this is my max',
    contentType: 'text',
    displayName: 'Payment: Budget Is Max',
    description: 'Budget flexibility option - this is max',
    category: 'form',
  },

  // Reservation actions
  {
    key: 'reservation.actions.goBack',
    value: 'Go Back',
    contentType: 'text',
    displayName: 'Reservation: Go Back',
    description: 'Go Back button in reservation dialog',
    category: 'action',
  },
  {
    key: 'reservation.actions.continue',
    value: 'Continue',
    contentType: 'text',
    displayName: 'Reservation: Continue',
    description: 'Continue button in reservation dialog',
    category: 'action',
  },
  {
    key: 'reservation.actions.next',
    value: 'Next',
    contentType: 'text',
    displayName: 'Reservation: Next',
    description: 'Next button in reservation dialog steps',
    category: 'action',
  },
  {
    key: 'reservation.actions.completeProfile',
    value: 'Complete Profile',
    contentType: 'text',
    displayName: 'Reservation: Complete Profile',
    description: 'Complete Profile button when profile is incomplete',
    category: 'action',
  },
  {
    key: 'reservation.actions.saveAndClose',
    value: 'Save & Close',
    contentType: 'text',
    displayName: 'Reservation: Save & Close',
    description: 'Save & Close button for pending members',
    category: 'action',
  },
  {
    key: 'reservation.actions.extendMembership',
    value: 'Extend Membership',
    contentType: 'text',
    displayName: 'Reservation: Extend Membership',
    description: 'Extend Membership button when date past term',
    category: 'action',
  },
  {
    key: 'reservation.actions.upgradeMembership',
    value: 'Upgrade Membership',
    contentType: 'text',
    displayName: 'Reservation: Upgrade Membership',
    description: 'Upgrade Membership button for non-subscribers',
    category: 'action',
  },
  {
    key: 'reservation.actions.submit',
    value: 'Submit',
    contentType: 'text',
    displayName: 'Reservation: Submit',
    description: 'Submit button for reservation requests',
    category: 'action',
  },
  {
    key: 'reservation.actions.save',
    value: 'Save',
    contentType: 'text',
    displayName: 'Reservation: Save',
    description: 'Save button for submitted reservations',
    category: 'action',
  },

  // Reservation validation (additional)
  {
    key: 'reservation.validation.agreePolicy',
    value: 'I agree to the CC hold & cancellation policy',
    contentType: 'text',
    displayName: 'Reservation: Agree Policy',
    description: 'Checkbox label for agreeing to hold & cancellation policy',
    category: 'validation',
  },
  {
    key: 'reservation.validation.datePastMembership',
    value: 'You selected a date past your membership term. Extend your membership or change the date to submit.',
    contentType: 'text',
    displayName: 'Reservation: Date Past Membership',
    description: 'Warning when selected date is past membership term',
    category: 'validation',
  },
  {
    key: 'reservation.validation.upgradeRequired',
    value: 'Upgrade to Premium/Platinum to submit this request, will be saved in the interim',
    contentType: 'text',
    displayName: 'Reservation: Upgrade Required',
    description: 'Warning when upgrade needed to submit',
    category: 'validation',
  },

  // Reservation preferred restaurant info
  {
    key: 'reservation.preferred.typicallyResponds',
    value: '{restaurant} typically responds within 24 hours',
    contentType: 'text',
    displayName: 'Preferred: Responds 24hrs',
    description: 'Text showing restaurant typical response time',
    category: 'help',
  },
  {
    key: 'reservation.preferred.preferredRestaurantsIn',
    value: 'Preferred Restaurants in {market}',
    contentType: 'text',
    displayName: 'Preferred: Restaurants In Market',
    description: 'Heading for preferred restaurants in a market',
    category: 'marketing',
  },
  {
    key: 'reservation.preferred.typicallyRespondHours',
    value: '(typically respond within 24 hours)',
    contentType: 'text',
    displayName: 'Preferred: Typically Respond',
    description: 'Subtext about typical response time',
    category: 'help',
  },
  {
    key: 'reservation.preferred.isMemberFavorite',
    value: 'is a member Favorite, not yet a Preferred restaurant',
    contentType: 'text',
    displayName: 'Preferred: Is Member Favorite',
    description: 'Text when single restaurant is not preferred (singular)',
    category: 'help',
  },
  {
    key: 'reservation.preferred.areMemberFavorites',
    value: 'are member Favorites, not yet Preferred restaurants',
    contentType: 'text',
    displayName: 'Preferred: Are Member Favorites',
    description: 'Text when multiple restaurants are not preferred (plural)',
    category: 'help',
  },
  {
    key: 'reservation.preferred.platinumCanRequest',
    value: 'Platinum members can request',
    contentType: 'text',
    displayName: 'Preferred: Platinum Can Request',
    description: 'Info that Platinum members can request non-preferred',
    category: 'help',
  },
  {
    key: 'reservation.preferred.notAcceptingRequests',
    value: 'Restaurant is not accepting requests via RM',
    contentType: 'text',
    displayName: 'Preferred: Not Accepting Requests',
    description: 'Warning when restaurant not accepting requests',
    category: 'validation',
  },
  {
    key: 'reservation.preferred.responseNotGuaranteed',
    value: 'Response not guaranteed',
    contentType: 'text',
    displayName: 'Preferred: Response Not Guaranteed',
    description: 'Warning for non-preferred restaurant responses',
    category: 'help',
  },
  {
    key: 'reservation.preferred.notAcceptingReferrals',
    value: 'not accepting request referrals, remove to proceed',
    contentType: 'text',
    displayName: 'Preferred: Not Accepting Referrals',
    description: 'Warning when restaurant not accepting referrals',
    category: 'validation',
  },
  {
    key: 'reservation.preferred.upgradeToPlatinum',
    value: 'Upgrade to Platinum',
    contentType: 'text',
    displayName: 'Preferred: Upgrade to Platinum',
    description: 'Link text to upgrade to Platinum membership',
    category: 'action',
  },
  {
    key: 'reservation.preferred.orSelectPreferred',
    value: 'or select only Preferred Restaurants to proceed',
    contentType: 'text',
    displayName: 'Preferred: Select Preferred Only',
    description: 'Alternative action text after upgrade link',
    category: 'help',
  },

  // Reservation private dining
  {
    key: 'reservation.private.fbBudgetRequired',
    value: 'Group/private dining requests require a Food & Beverage (F&B) budget',
    contentType: 'text',
    displayName: 'Private: F&B Budget Required',
    description: 'Heading explaining F&B budget requirement',
    category: 'help',
  },
  {
    key: 'reservation.private.higherBudgetExpedite',
    value: 'Higher budgets may expedite responses',
    contentType: 'text',
    displayName: 'Private: Higher Budget Note',
    description: 'Note that higher budgets expedite responses',
    category: 'help',
  },
  {
    key: 'reservation.private.inquirySent',
    value: 'Group/Private Dining Inquiry will be sent instantly to restaurants',
    contentType: 'text',
    displayName: 'Private: Inquiry Sent',
    description: 'Confirmation that inquiry will be sent instantly',
    category: 'help',
  },

  // Members page
  {
    key: 'members.masthead',
    value: 'Members: Artists, Athletes, CEOs, Investors, Restaurateurs',
    contentType: 'richtext',
    displayName: 'Members Page Masthead',
    description: 'Masthead heading on the Members page',
    category: 'marketing',
  },
  {
    key: 'members.tabs.howRmWorks',
    value: "<p>RM is a network of high-profile/high-spending members who highly value where they dine &amp; who restaurants love hosting</p><p>Our mission is to help members enjoy amazing dining experiences at their favorite places</p><p>Members use RM's AI-powered app to:</p><ul><li>Request &amp; access hard-to-get reservations (last-minute/prime times not available on OpenTable, SevenRooms/DoorDash, Resy, etc.) at their favorite/top places globally</li><li>Submit group/private dining inquiries — send 1 inquiry to up to 10 places</li><li>Discover just booked, trending &amp; their network's favorite restaurants</li><li>Track their favorite restaurants &amp; save notes (favorite dishes, seating, who recommended, etc.)</li><li>Connect with their favorite restaurants</li></ul>",
    contentType: 'richtext',
    displayName: 'Members Tab: How RM Works',
    description: 'Content for the How RM Works tab on Members page',
    category: 'help',
  },
  {
    key: 'members.tabs.faqs',
    value: '<p class=\'faq-title\'><b>1. Is there always a table available?</b></p><p>Restaurants make tables available if they\'re inspired to & open (not closed/bought-out) - who wouldn\'t for Taylor Swift, Michael Jordan, etc.?</p><p>RM Members regularly secure same-day/week reservations at top places if their requests are appealing, especially for parties of 2-4</p><p class=\'faq-title\'><b>2. What makes a request appealing?</b></p><p>Member profile &/or guaranteed F&B spend</p><p class=\'faq-subsection\'>Member profile: knowing who you are - bio, social links, affiliations/clubs/groups</p><p>Members of high-profile/select clubs/groups (e.g. NFLPA, YPO, Private Aviation, Golf/Social clubs & alike) can request a Verified profile - email <a href=\'mailto:preferred@reserverm.com\'>preferred@reserverm.com</a></p><p class=\'faq-subsection\'>Guaranteed F&B spend: higher amounts are of course more attractive - makes it a win-win</p><p>$200pp is avg on RM & ranges up to $2K+pp</p><p>Restaurants apply/waive at their sole discretion - RM takes no commission</p><p class=\'faq-title\'><b>3. What is the acceptance rate?</b></p><p>Varies by restaurant, party size & request</p><p>Acceptance rate is higher at Preferred Restaurants, also for Verified profiles &/or higher guaranteed F&B spends</p><p>RM does its best to get members\' requests considered - acceptance is at restaurant\'s sole discretion</p><p class=\'faq-title\'><b>4. How is my profile data used/monetized?</b></p><p>RM doesn\'t sell members\' data - we\'re member-subscription only</p><p>A few fields are required for requests - everything else optional but can & frequently does help</p>',
    contentType: 'richtext',
    displayName: 'Members Tab: FAQs',
    description: 'Content for the FAQs tab on Members page',
    category: 'help',
  },
  {
    key: 'members.tabs.howRmWorksTitle',
    value: 'Overview',
    contentType: 'text',
    displayName: 'Members Tab Title: How RM Works',
    description: 'Title of the "How RM Works" tab on the Members page',
    category: 'marketing',
  },
  {
    key: 'members.tabs.faqsTitle',
    value: 'FAQs',
    contentType: 'text',
    displayName: 'Members Tab Title: FAQs',
    description: 'Title of the "FAQs" tab on the Members page',
    category: 'marketing',
  },
  {
    key: 'members.testimonialsTitle',
    value: 'What members say:',
    contentType: 'text',
    displayName: 'Members Testimonials Title',
    description: 'Heading above the testimonials on the Members page',
    category: 'marketing',
  },

  // Restaurants page
  {
    key: 'restaurants.masthead',
    value: 'Members\' favorite/top places globally',
    contentType: 'richtext',
    displayName: 'Restaurants Page Masthead',
    description: 'Masthead text block on the Preferred Restaurants page',
    category: 'marketing',
  },
  {
    key: 'restaurants.tabs.howRmWorks',
    value: "<p>RM is a network of high-profile/high-spending members who highly value where they dine &amp; who restaurants love hosting</p><p>Our mission is to help members enjoy amazing dining experiences at their favorite places</p><p>Members use RM's AI-powered app for premium bookings &amp; discovery at their favorite/top restaurants globally</p><p>RM is free &amp; no commitment for restaurants — members are our customers</p><p>Restaurants respond to/accept requests at their sole discretion</p><p>Restaurants can activate a free restaurant account to view/connect with members who are their fans &amp; more (see Preferred Benefits)</p>",
    contentType: 'richtext',
    displayName: 'Restaurants Tab: How RM Works',
    description: 'Content for the How RM Works tab on Preferred Restaurants and restaurant info dialog',
    category: 'help',
  },
  {
    key: 'restaurants.tabs.preferredBenefits',
    value: '<p>Featured listing & elevated profile</p><p class=\'section-title\'>Showcase private & group dining spaces + post elevated experiences</p><p class=\'section-title\'>Gain insights - view profile details & preferences of members who are your fans</p><p class=\'section-title\'>Complimentary membership + Verified profile for executives/partners</p>',
    contentType: 'richtext',
    displayName: 'Restaurants Tab: Preferred Benefits',
    description: 'Content for the Preferred Benefits tab on Preferred Restaurants and restaurant info dialog',
    category: 'help',
  },
  {
    key: 'restaurants.tabs.faqs',
    value: '<p class=\'section-title\'><b>1. Why do members use RM?</b></p><p>They highly value dining at their favorites versus just what is available on OpenTable, Resy, etc.</p><p>Especially for business, dates & special occasions</p><p class=\'section-title\'><b>2. How did our restaurant get added to RM?</b></p><p>Members add their favorite restaurants to RM</p><p>If your restaurant is not listed & you want it added, email <a href=\'mailto:preferred@reserverm.com\'>preferred@reserverm.com</a></p><p class=\'section-title\'><b>3. How do we receive requests?</b></p><p>Requests are sent via email/text when members submit them in the app - RM reviews some requests before they\'re sent</p><p class=\'section-title\'><b>4. What is the cost & commitment?</b></p><p>No cost & no commitment for restaurants</p><p>Respond to/accept requests at your discretion - opt out anytime by emailing <a href=\'mailto:preferred@reserverm.com\'>preferred@reserverm.com</a></p><p class=\'section-title\'>Members (consumers) are our customer - our mission is to help them enjoy their favorites - ideally your restaurant too</p><p class=\'section-title\'><b>5. How is the spend guaranteed? What is RM\'s commission/fee?</b></p><p>Member\'s guaranteed spend secured via credit card hold</p><p>If not met, restaurants charge guaranteed spend on member\'s bill or receive remainder after from RM</p><p>Restaurants apply/waive at their discretion</p><p class=\'section-title\'>RM takes no commission/fee</p><p class=\'section-title\'><b>6. Which restaurants/cities are on RM?</b></p><p>Members have used RM for bookings at 300+ restaurants across 50+ cities globally</p><p>View Preferred & Just Booked on our <a href=\'/discover\'>home page</a></p><p class=\'section-title\'><b>7. How do we get added as a Preferred Restaurant?</b></p><p>Be a member\'s favorite & respond to requests within 24 hours</p><p>Request Restaurant Account to get started</p><p class=\'section-title\'>additional questions? email <a href=\'mailto:preferred@reserverm.com\'>preferred@reserverm.com</a></p>',
    contentType: 'richtext',
    displayName: 'Restaurants Tab: FAQs',
    description: 'Content for the FAQs tab on Preferred Restaurants and restaurant info dialog',
    category: 'help',
  },

  // Preferred request info dialog
  {
    key: 'preferredRequest.title',
    value: '<b>Hard-to-get reservations: <br>last minute/prime-times not available on OpenTable, Resy, etc.</b>',
    contentType: 'richtext',
    displayName: 'Preferred Request Dialog: Title',
    description: 'Heading at the top of the preferred-request info dialog (How RM Works / FAQs tabs reuse the members.tabs.* keys)',
    category: 'help',
  },
  {
    key: 'loginJoin.restaurant.loggedIn',
    value: 'Request a free restaurant account by emailing <a href=\'mailto:preferred@reserverm.com\'>preferred@reserverm.com</a>',
    contentType: 'richtext',
    displayName: 'Join Dialog: Restaurant Request (logged-in member)',
    description: 'Shown in the join dialog when an already-logged-in member clicks "Request Free Restaurant Account" (replaces the Request to Join wizard button)',
    category: 'help',
  },
  {
    key: 'menu.giveFriendsPlatinumAccess',
    value: 'Give Friends Platinum Access',
    contentType: 'text',
    displayName: 'Menu: Give Friends Platinum Access',
    description: 'Menu item label for the nominate friends section in profile menu',
    category: 'navigation',
  },
  {
    key: 'menu.referral.intro',
    value: '<p>Have friends who\'d love being a member too?</p><p>They can request access + 3 months free via your link below - Platinum is now referral-only.</p>',
    contentType: 'richtext',
    displayName: 'Referral: Intro Text',
    description: 'Introductory text in the nominate friends expandable section',
    category: 'marketing',
  },
  {
    key: 'menu.referral.referralMessage',
    value: 'Friends - I love using RM\'s app for hard-to-get reservations (last-minute/prime times) & think you will too. RM is a member network of Artists, Athletes, CEOs, Investors & Restaurateurs who highly value dining at favorite/top places globally. You can request access + 3 months free of Platinum here:',
    contentType: 'text',
    displayName: 'Referral: Referral Message',
    description: 'Referral message text shown in the referral box and used for clipboard copy (dynamic link appended separately)',
    category: 'marketing',
  },
  {
    key: 'menu.referral.faq',
    value: '<p style=\'font-weight: bold; margin-bottom: 4px;\'>FAQ: Will friends/others joining limit my access?</p><p>No - the more great members like you, the better. Faster responses & more tables for Instant Bookings.</p>',
    contentType: 'richtext',
    displayName: 'Referral: FAQ',
    description: 'FAQ section in the nominate friends expandable section',
    category: 'marketing',
  },
  {
    key: 'menu.referral.copyShare',
    value: 'Copy & share',
    contentType: 'text',
    displayName: 'Referral: Copy Button Text',
    description: 'Label for the copy referral message button',
    category: 'action',
  },
  {
    key: 'menu.referral.copied',
    value: 'Copied!',
    contentType: 'text',
    displayName: 'Referral: Copied Confirmation',
    description: 'Text shown after referral message is copied to clipboard',
    category: 'action',
  },
]

const flattenMessages = (obj, prefix = '') => {
  const out = []
  for (const [k, v] of Object.entries(obj)) {
    if (!prefix && EXCLUDED_TOP_LEVEL_KEYS.has(k)) continue
    const fullKey = prefix ? `${prefix}.${k}` : k
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenMessages(v, fullKey))
    } else if (typeof v === 'string') {
      out.push({ key: fullKey, value: v })
    }
  }
  
  return out
}

const inferContentType = value => (HTML_TAG_RE.test(value) ? 'richtext' : 'text')

const humanize = key =>
  key
    .split('.')
    .map(segment =>
      segment
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, ch => ch.toUpperCase()),
    )
    .join(' ')

// Ordered rules: first matching predicate wins. Each predicate receives
// { top, second, leaf, leafLc } parsed from the dotted key.
const CATEGORY_RULES = [
  [({ leaf }) => /btn$/i.test(leaf), 'action'],
  [({ leaf }) => ['logout', 'login', 'logIn', 'exitSimulation', 'requestToJoin'].includes(leaf), 'action'],
  [({ top }) => top === 'nav', 'navigation'],
  [({ top, second }) => top === 'home' && second === 'testimonials', 'testimonial'],
  [({ top, leafLc }) => top === 'home' && leafLc.endsWith('link'), 'navigation'],
  [({ top }) => top === 'home', 'marketing'],
  [({ top }) => top === 'toast', 'marketing'],
  [({ top, second, leaf }) => top === 'menu' && second === 'referral' && ['copyShare', 'copied'].includes(leaf), 'action'],
  [({ top, second }) => top === 'menu' && second === 'referral', 'marketing'],
  [({ top }) => top === 'menu', 'navigation'],
  [({ second }) => second === 'filters', 'filter'],
  [({ second }) => second === 'emptyState', 'empty-state'],
  [({ second }) => second === 'validation', 'validation'],
  [({ second }) => second === 'actions', 'action'],
  [({ second }) => second === 'labels', 'form'],
  [({ second }) => ['payment', 'preferred', 'private'].includes(second), 'help'],
  [({ top, second }) => second === 'tabs' && ['discover', 'bookings'].includes(top), 'navigation'],
  [({ second }) => second === 'tabs', 'help'],
  [({ top, second }) => ['favorites', 'bookings'].includes(top) && second === 'groups', 'navigation'],
  [({ top, leaf }) => ['favorites', 'bookings'].includes(top) && (leaf === 'newReservationCta' || leaf === 'tab'), leaf => (leaf === 'newReservationCta' ? 'action' : 'navigation')],
  [({ top }) => ['favorites', 'bookings'].includes(top), 'empty-state'],
  [({ top }) => top === 'discover', 'marketing'],
  [({ top }) => ['members', 'restaurants'].includes(top), 'marketing'],
  [({ top }) => top === 'reservation', 'form'],
  [({ top }) => top === 'footer', 'navigation'],
  [({ top }) => ['restaurant', 'agent', 'messaging'].includes(top), 'help'],
]

const inferCategory = key => {
  const parts = key.split('.')
  const ctx = {
    top: parts[0],
    second: parts[1],
    leaf: parts[parts.length - 1],
    leafLc: parts[parts.length - 1].toLowerCase(),
  }

  for (const [predicate, category] of CATEGORY_RULES) {
    if (predicate(ctx)) {
      return typeof category === 'function' ? category(ctx.leaf) : category
    }
  }

  return 'marketing'
}

const buildDerivedItem = ({ key, value }) => ({
  key,
  value,
  contentType: inferContentType(value),
  displayName: humanize(key),
  description: `i18n key: ${key}`,
  category: inferCategory(key),
})

const curatedKeys = new Set(curatedSeedItems.map(item => item.key))

const derivedSeedItems = flattenMessages(enMessages)
  .filter(({ key }) => !curatedKeys.has(key))
  .map(buildDerivedItem)

export const defaultSeedItems = [...curatedSeedItems, ...derivedSeedItems]

export default defaultSeedItems
